import React, { useState, useRef, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../../../context/AuthContext';
import { getFeedbacks, createFeedback, updateFeedback, deleteFeedback, toggleFeedbackResolve, updateMentionOnly } from '../../../../api/pmsApi';
import TodoRegisterLayer from '../../../../components/todo/TodoRegisterLayer';

// --- 추가된 애니메이션 스타일 ---
const style = document.createElement('style');
style.innerHTML = `
@keyframes blink-animation {
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
}
.blink-badge {
  animation: blink-animation 1.5s infinite ease-in-out;
}
@keyframes feed-glow-pulse {
  0% { box-shadow: 0 0 0 0px rgba(0, 0, 0, 0); }
  50% { box-shadow: 0 0 15px 5px rgba(60, 70, 80, 0.4); border-color: rgba(60, 70, 80, 0.8) !important; }
  100% { box-shadow: 0 0 0 0px rgba(0, 0, 0, 0); }
}
.highlight-feed {
  animation: feed-glow-pulse 1s ease-in-out 3;
  transition: all 0.3s ease;
}
`;
document.head.appendChild(style);
// --- 아바타 색상 로직 ---
const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];
const getAvatarColorInfo = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const index = hash % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[index];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

const Avatar = ({ name, photo, size = 38 }) => {
  const info = getAvatarColorInfo(name);
  const initial = name.charAt(0);
  return (
    <div className={`d-flex align-items-center justify-content-center text-${info.text} rounded-circle fw-bolder shadow-sm flex-shrink-0 overflow-hidden`}
      style={{ width: `${size}px`, height: `${size}px`, backgroundColor: photo ? 'transparent' : info.bg, fontSize: `${size * 0.4}px`, userSelect: 'none' }}>
      {photo ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}
    </div>
  );
};

// --- 시간 포맷터 ---
const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const now = new Date();
  const diffMs = now - date;
  if (diffMs < 0) return '방금 전';
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return '방금 전';
  if (diffHour < 1) return `${diffMin}분 전`;
  if (diffDay < 1) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;

  return date.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function Feedback({ project, currentUser, allProjectMembers, isPureWatcher, onDataRefresh }) {
  const { token } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [inputText, setInputText] = useState('');
  const [commentType, setCommentType] = useState('general'); // general, request, notice
  const [targetUserId, setTargetUserId] = useState([]);
  const [showMemberPopover, setShowMemberPopover] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');

  // 폼이 일반/공지 등으로 바뀌거나 리셋될 때 혹시라도 남아있을 대상자를 완벽히 초기화
  useEffect(() => {
    if (commentType !== 'request') {
      setTargetUserId([]);
      setShowMemberPopover(false);
    }
  }, [commentType]);

  const toggleTargetSelection = (user) => {
    if (!user) {
      setTargetUserId([]);
      return;
    }
    const uid = user.id;
    setTargetUserId(prev => {
      if (prev.includes(uid)) return prev.filter(id => id !== uid);
      return [...prev, uid];
    });
  };

  const [replyTarget, setReplyTarget] = useState(null); // { parentId, mentionName }
  const [replyText, setReplyText] = useState(''); // 인라인 답글용 텍스트 상태

  const [editTargetId, setEditTargetId] = useState(null);
  const [editText, setEditText] = useState('');

  const [todoSource, setTodoSource] = useState(null);
  const todoAnchorEl = useRef(null);

  const handleOpenTodoRegister = (e, item) => {
    e.stopPropagation();
    todoAnchorEl.current = e.currentTarget;
    let typeName = '일반';
    if (item.type === 'request') typeName = '요청';
    else if (item.type === 'notice') typeName = '공지';

    // API 응답 구조(Camel/Snake) 모두 완벽 방어
    const isReply = !!(item.parent_id || item.parentId);
    const mentionName = item.target_mention || item.targetMention;

    let titleStr = `피드 - ${typeName}`;
    if (isReply) {
      if (mentionName) {
        titleStr += `[답글 - @${mentionName}]`;
      } else {
        titleStr += `[답글]`;
      }
    }

    setTodoSource({
      ref_type: 'pms_feed',
      ref_table: 'pms_project_feedback',
      ref_id: item.id,
      title: titleStr,
      memo: item.content || '',
      add_title: project?.title || '이름 없음',
      add_id1: project?.id,
    });
  };

  // 필터 상태 추가
  const [filterType, setFilterType] = useState('all'); // all, general, notice, request
  const [filterResolve, setFilterResolve] = useState('all'); // all, unresolved, resolved

  const [highlightFeedId, setHighlightFeedId] = useState(null); // 완벽한 하이라이트 상태 제어용
  const [isLoading, setIsLoading] = useState(true); // 피드 로딩 상태

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const prevCommentsLengthRef = useRef(comments.length);


  const location = useLocation();

  // [추가] 내 관련 알림만 받기 상태 관리 (초기값 true, 부모 컴포넌트나 멤버 데이터에서 받아올 수 있다면 좋지만 현재로선 로컬 상태 우선 관리)
  // 향후 백엔드에서 currentUser의 mention_only 값을 반환해주면 그걸 초기값으로 사용하도록 개선할 수 있습니다.
  const [mentionOnly, setMentionOnly] = useState(true);

  // 내 멤버 정보에서 초기값 세팅
  useEffect(() => {
    if (project?.members && currentUser) {
      const meRecord = project.members.find(m => m.user_id === currentUser.id || m.user?.id === currentUser.id);
      if (meRecord && meRecord.mention_only !== undefined) {
        setMentionOnly(Boolean(meRecord.mention_only));
      }
    }
  }, [project, currentUser]);

  const handleMentionOnlyToggle = async (e) => {
    const newVal = e.target.checked;
    setMentionOnly(newVal);
    if (!project || !project.id || !token) return;
    try {
      await updateMentionOnly(project.id, newVal ? 1 : 0, token);

      // 부모로부터 내려받은 project 객체의 원본 배열 값을 직접 수정하여, 다른 탭에 갔다가 돌아와도 체크 상태가 유지되도록 즉시 캐싱 처리
      if (project?.members && currentUser) {
        const meRecord = project.members.find(m => m.user_id === currentUser.id || m.user?.id === currentUser.id);
        if (meRecord) meRecord.mention_only = newVal ? 1 : 0;
      }
    } catch (err) {
      console.error('알림 설정 변경 실패:', err);
      // 실패 시 롤백
      setMentionOnly(!newVal);
    }
  };

  useEffect(() => {
    const loadFeedbacks = async () => {
      if (!project || !project.id || !token) return;
      setIsLoading(true);
      try {
        const res = await getFeedbacks(project.id, token);
        if (res.ok) {
          setComments(res.data);
          // 최초 데이터 세팅 시에는 스크롤 효과가 발생하지 않도록 이전 길이 상태를 동기화
          prevCommentsLengthRef.current = res.data.length;
        }
      } catch (err) {
        console.error('피드백 목록 조회 실패:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadFeedbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, token]);

  // --- [추가] 외부(To-Do 원본보기 등)에서 넘어왔을 때 해당 항목으로 스크롤 및 하이라이트 ---
  const focusHandledRef = useRef(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const focusId = searchParams.get('focusId');
    const tab = searchParams.get('tab');
    const triggerKey = location.state?.notificationTrigger || focusId;

    if (comments && comments.length > 0) {
      if (focusId && tab === 'comments' && focusHandledRef.current !== triggerKey) {
        // focusHandledRef를 스크롤 성공 후에 세팅
        // 이유: 인터벌이 useEffect cleanup으로 중간에 사망해도 다음 run이 handled=null을 보고 재시도 가능
        searchParams.delete('focusId');
        const newUrl = location.pathname + '?' + searchParams.toString();
        window.history.replaceState(null, '', newUrl.replace(/\?$/, ''));

        let attempts = 0;
        const tryScroll = setInterval(() => {
          attempts++;
          const el = document.getElementById(`feed-${focusId}`);
          const container = scrollRef.current;

          if (el && container && container.clientHeight > 0) {
            clearInterval(tryScroll);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const target = document.getElementById(`feed-${focusId}`);
                const cont = scrollRef.current;
                if (target && cont) {
                  const contRect = cont.getBoundingClientRect();
                  const targetRect = target.getBoundingClientRect();
                  cont.scrollTop = cont.scrollTop + (targetRect.top - contRect.top) - (contRect.height / 2) + (targetRect.height / 2);
                  focusHandledRef.current = triggerKey; // 스크롤 성공 후 세팅 (중복 방지)
                  setHighlightFeedId(focusId);
                  // 스크롤 및 하이라이트 부여가 '완료된 시점'부터 정확히 3초 뒤에 꺼지도록 타이머 위치 이동
                  setTimeout(() => { setHighlightFeedId(null); }, 3100);
                }
              });
            });
          }
          if (attempts >= 30) clearInterval(tryScroll);
        }, 100);

        return () => clearInterval(tryScroll);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, location.search, location.pathname, location.key]);

  useEffect(() => {
    // 이전 길이 상태는 유지할 수 있으나, 더이상 감지 자동 스크롤에는 쓰지 않음
    prevCommentsLengthRef.current = comments.length;
  }, [comments]);

  const handleSubmit = async () => {
    if (!inputText.trim() || !project || !project.id) return;
    try {
      const targetIds = (commentType === 'request' && targetUserId.length > 0) ? targetUserId : null;
      const data = {
        content: inputText,
        comment_type: commentType,
        target_user_id: targetIds,
        parent_id: null,
        target_mention: null,
      };
      const res = await createFeedback(project.id, data, token);
      if (res.ok) {
        setComments([...comments, res.data]);
        setInputText('');
        setCommentType('general');
        setTargetUserId([]);
        setShowMemberPopover(false);
        setMemberSearchTerm('');

        // 최상위 코멘트 등록 시에만 스크롤을 맨 위로 부드럽게 이동
        setTimeout(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 50);
        if (onDataRefresh) onDataRefresh();
      }
    } catch (err) {
      alert('피드백 생성에 실패했습니다.');
    }
  };

  const handleInlineReplySubmit = async (parentId) => {
    if (!replyText.trim() || !project || !project.id) return;
    try {
      const data = {
        content: replyText,
        comment_type: 'general',
        target_user_id: null,
        parent_id: parentId,
        target_mention: replyTarget?.mentionName || null,
      };
      const res = await createFeedback(project.id, data, token);
      if (res.ok) {
        setComments([...comments, res.data]);
        setReplyText('');
        setReplyTarget(null);
        if (onDataRefresh) onDataRefresh();
      }
    } catch (err) {
      alert('답글 생성에 실패했습니다.');
    }
  };

  const toggleResolve = async (id) => {
    try {
      const res = await toggleFeedbackResolve(id, token);
      if (res.ok) {
        setComments(comments.map(c => c.id === id ? res.data : c));
        if (onDataRefresh) onDataRefresh();
      }
    } catch (err) {
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까? (하위 답글도 모두 삭제됩니다)')) return;
    try {
      const res = await deleteFeedback(id, token);
      if (res.ok) {
        setComments(comments.filter(c => c.id !== id && c.parentId !== id));
        if (editTargetId === id) setEditTargetId(null);
        if (replyTarget?.parentId === id) setReplyTarget(null);
        if (onDataRefresh) onDataRefresh();
      }
    } catch (err) {
      alert('삭제에 실패했습니다.');
    }
  };

  const startEdit = (c) => { setEditTargetId(c.id); setEditText(c.content); setReplyTarget(null); };

  const saveEdit = async (id) => {
    try {
      const res = await updateFeedback(id, { content: editText }, token);
      if (res.ok) {
        setComments(comments.map(c => c.id === id ? res.data : c));
        setEditTargetId(null);
        setEditText('');
      }
    } catch (err) {
      alert('수정에 실패했습니다.');
    }
  };

  const rootComments = comments
    .filter(c => !c.parentId)
    .filter(c => {
      if (filterType === 'my_feed') {
        const uIdStr = String(currentUser?.id);
        const uLoginIdStr = String(currentUser?.userid);
        const myMemberRecord = (allProjectMembers || []).find(m =>
          String(m.user_id) === uIdStr || String(m.user?.id) === uIdStr || String(m.user?.userid) === uLoginIdStr
        );
        const myNumericId = myMemberRecord ? String(myMemberRecord.user_id) : null;

        const feedAuthorId = String(c.author?.id || c.user_id || c.author?.userid);
        const isAuthor = (feedAuthorId === uIdStr || feedAuthorId === uLoginIdStr || feedAuthorId === myNumericId);

        if (!isAuthor) return false;
      } else if (filterType !== 'all' && c.type !== filterType) {
        return false;
      }

      if (filterType === 'request' && filterResolve !== 'all') {
        if (filterResolve === 'unresolved' && c.isResolved) return false;
        if (filterResolve === 'resolved' && !c.isResolved) return false;
        if (filterResolve === 'awaiting') {
          if (c.isResolved) return false;

          const uIdStr = String(currentUser?.id);
          const uLoginIdStr = String(currentUser?.userid);
          const myMemberRecord = (allProjectMembers || []).find(m =>
            String(m.user_id) === uIdStr || String(m.user?.id) === uIdStr || String(m.user?.userid) === uLoginIdStr
          );
          const myNumericId = myMemberRecord ? String(myMemberRecord.user_id) : null;

          let targetIds = [];
          if (Array.isArray(c.targetUserId)) {
            targetIds = c.targetUserId;
          } else if (typeof c.targetUserId === 'string') {
            try {
              targetIds = JSON.parse(c.targetUserId);
              if (!Array.isArray(targetIds)) targetIds = [targetIds];
            } catch {
              targetIds = c.targetUserId.split(',').map(s => s.trim());
            }
          } else if (c.targetUserId) {
            targetIds = [c.targetUserId];
          }

          const isAllTarget = targetIds.length === 0;
          const isExplicitlyTargeted = targetIds.map(String).some(id => id === uIdStr || id === uLoginIdStr || id === myNumericId);

          const feedAuthorId = String(c.author?.id || c.user_id || c.author?.userid);
          const isAuthor = (feedAuthorId === uIdStr || feedAuthorId === uLoginIdStr || feedAuthorId === myNumericId);
          if (isAuthor) return false; // 내가 쓴 글이면 무조건 제외
          if (!isAllTarget && !isExplicitlyTargeted) return false;

          const myReplies = comments.filter(reply => {
            const replyAuthorId = String(reply.author?.id || reply.user_id || reply.author?.userid);
            return (String(reply.parentId) === String(c.id) || String(reply.parent_id) === String(c.id)) &&
              (replyAuthorId === uIdStr || replyAuthorId === uLoginIdStr || replyAuthorId === myNumericId);
          });

          if (myReplies.length > 0) return false;
        }
      }
      return true;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="d-flex flex-column bg-white border border-secondary border-opacity-25 rounded-4 shadow-sm h-100 overflow-hidden" style={{ fontFamily: '"Pretendard", "Noto Sans KR", sans-serif' }}>
      <style>{`
        .feedback-comment-row:hover .comment-actions { opacity: 1 !important; }
        .mention-text { color: #4c6ef5; font-weight: 600; }
        
        .reply-connector {
          position: absolute;
          top: 0;
          left: -24px;
          width: 24px;
          height: 17px;
          border-left: 2px solid #dee2e6;
          border-bottom: 2px solid #dee2e6;
          border-bottom-left-radius: 8px;
        }

        /* 완전히 새로운 3가지 파스텔 컬러 테마 커스텀 버튼 */
        .btn-outline-custom-gen { color: #339af0; border-color: #339af0; }
        .btn-check:checked + .btn-outline-custom-gen { background-color: #339af0; color: white; border-color: #339af0; }
        
        .btn-outline-custom-req { color: #20c997; border-color: #20c997; }
        .btn-check:checked + .btn-outline-custom-req { background-color: #20c997; color: white; border-color: #20c997; }
        
        .btn-outline-custom-not { color: #ff922b; border-color: #ff922b; }
        .btn-check:checked + .btn-outline-custom-not { background-color: #ff922b; color: white; border-color: #ff922b; }
        
        /* 통합형 입력 박스 (Slack 스타일) */
        .unified-input-box {
          border: 1px solid #ced4da;
          border-radius: 8px;
          background-color: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .unified-input-box:focus-within {
          border-color: #4c6ef5;
          box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.15);
        }
      `}</style>

      {/* 헤더 (왼쪽 패널처럼 깔끔한 화이트) */}
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-white position-relative">
        <div className="d-flex align-items-center gap-3">
          <h5 className="m-0 fw-bolder text-dark text-nowrap"><i className="fas fa-comments text-primary me-2"></i>피드 라운지</h5>

          {/* 타인 멘션 알림 끄기 토글 */}
          <div className="form-check form-switch mb-0 d-flex align-items-center" title="체크 시 원글 작성자나 지정 대상자에게 가는 알림을 모두 무시하고 오직 '바로 윗글 작성자' 딱 1명에게만 1:1로 알림이 갑니다.">
            <input
              className="form-check-input mt-0 me-2"
              type="checkbox"
              id="mentionOnlyToggle"
              checked={mentionOnly}
              onChange={handleMentionOnlyToggle}
              style={{ cursor: 'pointer', width: '1.6rem', height: '0.8rem' }}
            />
            <label className="form-check-label text-secondary fw-bold mb-0" htmlFor="mentionOnlyToggle" style={{ fontSize: '0.75rem', cursor: 'pointer', userSelect: 'none' }}>
              내 답글 알림만 받기
            </label>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* 타입 필터 드롭다운 */}
          <select
            className="form-select form-select-sm shadow-sm border-secondary border-opacity-25 fw-bold text-secondary"
            style={{ width: '110px', cursor: 'pointer' }}
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setFilterResolve('all');
            }}
          >
            <option value="all">유형: 전체</option>
            <option value="general">일반</option>
            <option value="notice">공지</option>
            <option value="request">요청</option>
            <hr />
            <option value="my_feed">내 등록 피드</option>
          </select>

          {/* 완료 상태 필터 드롭다운 (요청일 때만 표시) */}
          {filterType === 'request' && (
            <select
              className="form-select form-select-sm shadow-sm border-secondary border-opacity-25 fw-bold text-secondary"
              style={{ width: '100px', cursor: 'pointer' }}
              value={filterResolve}
              onChange={(e) => setFilterResolve(e.target.value)}
            >
              <option value="all">상태: 모두</option>
              <option value="unresolved">미완료</option>
              <option value="resolved">완료</option>
              <hr />
              <option value="awaiting">답변 대기</option>
            </select>
          )}
        </div>
      </div>

      {/* 타임라인 피드 */}
      <div className="flex-grow-1 px-3 pt-3 overflow-auto" ref={scrollRef}>
        <div style={{ paddingBottom: '12px' }}>
          {isLoading && (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: '300px' }}>
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem', borderWidth: '0.2rem', opacity: 0.7 }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <h6 className="fw-bold text-secondary">피드를 불러오는 중입니다...</h6>
            </div>
          )}
          {!isLoading && rootComments.length === 0 && (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: '200px' }}>
              <div className="mb-3" style={{ fontSize: '48px', color: '#dee2e6' }}>
                <i className="fas fa-comment-dots"></i>
              </div>
              <h6 className="fw-bolder text-secondary mb-1">아직 등록된 피드가 없습니다.</h6>
              <p className="small mb-0">첫 번째 의견이나 요청을 남겨보세요!</p>
            </div>
          )}
          {!isLoading && rootComments.map((root, index) => {
            const renderInlineReplyBox = (targetId) => {
              if (replyTarget?.targetId !== targetId) return null;
              return (
                <div className="mt-2 pt-2" style={{ borderTop: '1px dashed #ced4da' }}>
                  <div className="d-flex gap-2 align-items-stretch">
                    <textarea
                      className="form-control form-control-sm shadow-none"
                      rows="2"
                      placeholder={replyTarget.mentionName ? `@${replyTarget.mentionName}님에게 답글...` : "답글을 입력하세요..."}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da', flexGrow: 1 }}
                      autoFocus
                    ></textarea>
                    <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1" style={{ backgroundColor: '#4c6ef5', fontSize: '11px', lineHeight: '1' }} onClick={() => handleInlineReplySubmit(root.id)}>저장</button>
                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1" style={{ backgroundColor: '#868e96', fontSize: '11px', lineHeight: '1' }} onClick={() => { setReplyTarget(null); setReplyText(''); }}>취소</button>
                    </div>
                  </div>
                </div>
              );
            };

            const replies = comments
              .filter(c => c.parentId === root.id)
              .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            const isRequest = root.type === 'request';
            const isNotice = root.type === 'notice';
            const hasMyReply = replies.some(r => r.author?.id === currentUser?.id);

            const targetIds = Array.isArray(root.targetUserId) ? root.targetUserId : (root.targetUserId ? [root.targetUserId] : []);
            let targetUsers = targetIds
              .map(id => (allProjectMembers || []).find(u => u.id === id))
              .filter(Boolean)
              .sort((a, b) => a.name.localeCompare(b.name, 'ko'));

            // 본인(currentUser)이 대상에 포함되어 있다면 뱃지 대표 이름으로 표시하기 위해 맨 앞으로 이동
            const myIndex = targetUsers.findIndex(u => u.id === currentUser?.id);
            if (myIndex > 0) {
              const myUser = targetUsers.splice(myIndex, 1)[0];
              targetUsers.unshift(myUser);
            }

            let targetText = '전체';
            // 전체 툴팁에는 본인 여부 상관없이 가나다순 원래 정렬 배열 사용
            const displayUsers = targetUsers.length > 0 ? [...targetUsers].sort((a, b) => a.name.localeCompare(b.name, 'ko')) : (allProjectMembers || []).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
            const amITarget = displayUsers.some(u => u.id === currentUser?.id);
            const amIAuthor = root.author?.id === currentUser?.id;
            const shouldBlink = !root.isResolved && !hasMyReply && amITarget && !amIAuthor;

            // HomeTab.js와 100% 동일한 커스텀 툴팁 (다크 테마)
            let customTooltipNode = null;
            if (targetUsers.length > 1 || targetUsers.length === 0) {
              customTooltipNode = (
                <div className="custom-tooltip position-absolute shadow-lg rounded-3 p-2"
                  style={{
                    top: '100%', left: '0', width: 'max-content', maxWidth: '250px',
                    marginTop: '8px', zIndex: 1050, backgroundColor: '#343a40', color: '#f8f9fa', pointerEvents: 'none',
                    opacity: 0, visibility: 'hidden', transition: 'opacity 0.2s'
                  }}>
                  <div style={{ position: 'absolute', top: '-4px', left: '20px', transform: 'rotate(45deg)', width: '8px', height: '8px', backgroundColor: '#343a40' }}></div>
                  <div className="mb-1 text-start">
                    <span className="badge bg-primary px-1 border border-primary border-opacity-50" style={{ fontSize: '0.65rem' }}>요청 대상</span>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                    {displayUsers.map(u => (
                      <span key={u.id} className="rounded" style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '2px 6px', fontSize: '0.7rem', wordBreak: 'keep-all', color: '#e9ecef', fontWeight: 'normal' }}>
                        {u.name}/{u.org?.position || u.position || ''}
                      </span>
                    ))}
                  </div>
                </div>
              );
            }

            if (targetUsers.length === 1) {
              targetText = `@${targetUsers[0].name}`;
            } else if (targetUsers.length > 1) {
              targetText = `@${targetUsers[0].name} 외 ${targetUsers.length - 1}명`;
            }

            /* 
               통합형 카드 테마 (옵션 1번)
               원문과 답글을 하나의 카드로 묶기 위해 컬러와 테두리 분리
            */
            let rootBgColor = '#e7f5ff'; let rootBorder = '1px solid #a5d8ff'; // 일반
            let rootOpacity = 1;

            if (isNotice) {
              rootBgColor = '#fff4e6'; rootBorder = '1px solid #ffd8a8'; // 공지
            } else if (isRequest && !root.isResolved) {
              rootBgColor = '#e6fcf5'; rootBorder = '1px solid #96f2d7'; // 요청
            } else if (isRequest && root.isResolved) {
              rootBgColor = '#f1f3f5'; rootBorder = '1px solid #ced4da'; // 해결됨
              rootOpacity = 0.8;
            }

            return (
              <div id={`feed-${root.id}`} key={root.id} className={`rounded-4 ${index === rootComments.length - 1 ? 'mb-0' : 'mb-3'} ${highlightFeedId === String(root.id) ? 'highlight-feed' : ''}`} style={{ border: rootBorder, opacity: rootOpacity }}>
                {/* 본문 (Root) - 상단 컬러 헤더 역할 */}
                <div className={`py-2 px-3 feedback-comment-row position-relative`} style={{ backgroundColor: rootBgColor, borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit', borderBottomLeftRadius: replies.length === 0 ? 'inherit' : 0, borderBottomRightRadius: replies.length === 0 ? 'inherit' : 0 }}>

                  <div className="d-flex align-items-start">
                    <Avatar name={root.author.name} photo={root.author.org?.photo_path || root.author.photo_path} size={30} />

                    <div className="ms-3 flex-grow-1">
                      {/* 상단 뱃지 및 메타 영역 */}
                      <div className="d-flex align-items-center mb-1">
                        <div className="d-flex align-items-center gap-2">
                          {isNotice && <span className="badge px-2 py-1 flex-shrink-0" style={{ fontSize: '11px', backgroundColor: '#f59f00', color: '#fff' }}><i className="fas fa-bullhorn me-1"></i>공지</span>}
                          {isRequest && (targetUsers.length > 1 || targetUsers.length === 0) ? (
                            <div className="position-relative d-inline-block"
                              onMouseEnter={(e) => { e.currentTarget.querySelector('.custom-tooltip').style.opacity = 1; e.currentTarget.querySelector('.custom-tooltip').style.visibility = 'visible'; }}
                              onMouseLeave={(e) => { e.currentTarget.querySelector('.custom-tooltip').style.opacity = 0; e.currentTarget.querySelector('.custom-tooltip').style.visibility = 'hidden'; }}>
                              <span className={`badge px-2 py-1 text-truncate flex-shrink-0 ${shouldBlink ? 'blink-badge' : ''}`} style={{ fontSize: '11px', maxWidth: '200px', cursor: 'pointer', ...(root.isResolved ? { backgroundColor: '#40c057', color: '#fff' } : { backgroundColor: '#20c997', color: '#fff' }) }}>
                                {root.isResolved ? <><i className="fas fa-check-circle me-1"></i>처리 완료</> : <><i className="fas fa-tasks me-1"></i>요청: {targetText}</>}
                              </span>
                              {customTooltipNode}
                            </div>
                          ) : isRequest ? (
                            <span className={`badge px-2 py-1 text-truncate flex-shrink-0 ${shouldBlink ? 'blink-badge' : ''}`} style={{ fontSize: '11px', maxWidth: '200px', ...(root.isResolved ? { backgroundColor: '#40c057', color: '#fff' } : { backgroundColor: '#20c997', color: '#fff' }) }}>
                              {root.isResolved ? <><i className="fas fa-check-circle me-1"></i>처리 완료</> : <><i className="fas fa-tasks me-1"></i>요청: {targetText}</>}
                            </span>
                          ) : null}
                          {!isNotice && !isRequest && (
                            <span className="badge px-2 py-1 flex-shrink-0" style={{ fontSize: '11px', backgroundColor: '#339af0', color: '#fff' }}>
                              <i className="fas fa-comment-dots me-1"></i>일반
                            </span>
                          )}
                          <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '13px' }}>{root.author.name}</span>
                          <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '12px' }}>{root.author.position}</span>
                          <span className="text-muted text-nowrap flex-shrink-0" style={{ fontSize: '11px' }}>{getRelativeTime(root.createdAt)}</span>
                        </div>

                        {/* 액션 버튼 (오버레이) */}
                        {!isPureWatcher && (
                          <div
                            className={`comment-actions transition-opacity d-flex gap-1 align-items-center ms-2 ${(todoSource && todoSource.ref_id === root.id) ? 'opacity-100' : 'opacity-0'}`}
                            style={{ position: 'absolute', top: '4px', right: '12px', backgroundColor: rootBgColor, paddingLeft: '8px', zIndex: 10 }}
                          >
                            {/* 권한 제어: 해결완료/취소 버튼 (작성자 본인 OR 전체대상 OR 지정대상자) */}
                            {(() => {
                              if (!isRequest) return null;
                              const isAllTarget = targetUsers.length === 0;
                              const isTargetOrAuthor = isAllTarget || targetIds.includes(currentUser?.id) || root.author?.id === currentUser?.id;

                              if (!isTargetOrAuthor) return null;

                              return (
                                <>
                                  {!root.isResolved && (
                                    <button className="btn btn-sm py-0 px-2 rounded fw-bold" style={{ backgroundColor: '#40c057', color: '#fff', fontSize: '12px', height: '24px' }} onClick={() => toggleResolve(root.id)}>
                                      <i className="fas fa-check me-1"></i>완료 처리
                                    </button>
                                  )}
                                  {root.isResolved && (
                                    <button className="btn btn-sm btn-outline-secondary py-0 px-2 rounded fw-bold" style={{ fontSize: '12px', height: '24px' }} onClick={() => toggleResolve(root.id)}>
                                      <i className="fas fa-undo me-1"></i>취소
                                    </button>
                                  )}
                                </>
                              );
                            })()}

                            <button className="btn btn-sm text-secondary p-1" onClick={() => { setReplyTarget({ parentId: root.id, targetId: root.id, mentionName: null }); setReplyText(''); setEditTargetId(null); }} title="답글">
                              <i className="fas fa-reply fs-6"></i>
                            </button>

                            <div style={{ position: 'relative' }}>
                              <button className="btn btn-sm text-secondary p-1" onClick={(e) => handleOpenTodoRegister(e, root)} title="To-Do 등록">
                                <i className="fas fa-tasks fs-6"></i>
                              </button>
                              {todoSource && todoSource.ref_id === root.id && (
                                <TodoRegisterLayer
                                  source={todoSource}
                                  anchorRef={todoAnchorEl}
                                  onClose={() => setTodoSource(null)}

                                  direction="down"
                                  align="right"
                                />
                              )}
                            </div>

                            {/* 권한 제어: 수정 (작성자 본인만), 삭제 (작성자 본인 OR PM) */}
                            {root.author?.id === currentUser?.id && (
                              <button className="btn btn-sm text-secondary p-1" onClick={() => startEdit(root)} title="수정"><i className="fas fa-edit fs-6"></i></button>
                            )}
                            {(root.author?.id === currentUser?.id || (project && String(project.author_id) === String(currentUser?.id))) && (
                              <button className="btn btn-sm text-danger p-1" onClick={() => handleDelete(root.id)} title="삭제"><i className="fas fa-trash-alt fs-6"></i></button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 텍스트 내용 */}
                      {editTargetId === root.id ? (
                        <div className="d-flex flex-column gap-2 mt-2">
                          <textarea className="form-control form-control-sm" rows="2" value={editText} onChange={e => setEditText(e.target.value)}></textarea>
                          <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-sm text-white rounded-1 px-2 fw-bold shadow-sm" style={{ backgroundColor: '#4c6ef5', fontSize: '11px', height: '26px', lineHeight: '1' }} onClick={() => saveEdit(root.id)}>저장</button>
                            <button className="btn btn-sm text-white rounded-1 px-2 fw-bold shadow-sm" style={{ backgroundColor: '#868e96', fontSize: '11px', height: '26px', lineHeight: '1' }} onClick={() => setEditTargetId(null)}>취소</button>
                          </div>
                        </div>
                      ) : (
                        <div className={`text-dark lh-base mt-1 ${isRequest && root.isResolved ? 'text-decoration-line-through text-muted' : ''}`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                          {root.content}
                        </div>
                      )}
                      {renderInlineReplyBox(root.id)}
                    </div>
                  </div>
                </div>

                {/* 답글 (Replies) - 원문과 이어지는 하단 화이트 바디 역할 */}
                {replies.length > 0 && (
                  <div className="py-2 pe-3" style={{ paddingLeft: '32px', borderTop: rootBorder, backgroundColor: '#fdfdfd', borderBottomLeftRadius: 'inherit', borderBottomRightRadius: 'inherit' }}>
                    {replies.map((reply, index) => (
                      <div id={`feed-${reply.id}`} key={reply.id} className={`py-1 feedback-comment-row position-relative d-flex ${index > 0 ? 'mt-1' : ''} ${highlightFeedId === String(reply.id) ? 'highlight-feed' : ''}`}>
                        <div className="reply-connector" style={{ left: '-12px', width: '12px' }}></div>

                        <Avatar name={reply.author?.name || 'Unknown'} photo={reply.author?.org?.photo_path || reply.author?.photo_path} size={26} />

                        <div className="ms-3 flex-grow-1 min-w-0">
                          <div className="d-flex align-items-center mb-1">
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '13px' }}>{reply.author?.name || 'Unknown'}</span>
                              <span className="fw-bold text-dark text-nowrap flex-shrink-0" style={{ fontSize: '12px' }}>{reply.author?.position || ''}</span>
                              <span className="text-muted text-nowrap flex-shrink-0" style={{ fontSize: '11px' }}>{getRelativeTime(reply.createdAt)}</span>
                            </div>

                            {/* 답글 액션 버튼 */}
                            {!isPureWatcher && (
                              <div
                                className={`comment-actions transition-opacity d-flex gap-1 align-items-center ms-2 ${(todoSource && todoSource.ref_id === reply.id) ? 'opacity-100' : 'opacity-0'}`}
                                style={{ position: 'absolute', top: '2px', right: '12px', backgroundColor: '#fdfdfd', paddingLeft: '8px', zIndex: 10 }}
                              >
                                <button className="btn btn-sm text-secondary p-0 px-1" onClick={() => { setReplyTarget({ parentId: root.id, targetId: reply.id, mentionName: reply.author?.name }); setReplyText(''); setEditTargetId(null); }} title="이 사람에게 답글">
                                  <i className="fas fa-reply fs-7"></i>
                                </button>
                                <div style={{ position: 'relative' }}>
                                  <button className="btn btn-sm text-secondary p-0 px-1" onClick={(e) => handleOpenTodoRegister(e, reply)} title="To-Do 등록">
                                    <i className="fas fa-tasks fs-7"></i>
                                  </button>
                                  {todoSource && todoSource.ref_id === reply.id && (
                                    <TodoRegisterLayer
                                      source={todoSource}
                                      anchorRef={todoAnchorEl}
                                      onClose={() => setTodoSource(null)}

                                      direction="down"
                                      align="right"
                                    />
                                  )}
                                </div>
                                {/* 권한 제어: 수정 (작성자 본인만), 삭제 (작성자 본인 OR PM) */}
                                {reply.author?.id === currentUser?.id && (
                                  <button className="btn btn-sm text-secondary p-0 px-1" onClick={() => startEdit(reply)} title="수정"><i className="fas fa-edit fs-7"></i></button>
                                )}
                                {(reply.author?.id === currentUser?.id || (project && String(project.author_id) === String(currentUser?.id))) && (
                                  <button className="btn btn-sm text-danger p-0 px-1" onClick={() => handleDelete(reply.id)} title="삭제"><i className="fas fa-trash-alt fs-7"></i></button>
                                )}
                              </div>
                            )}
                          </div>

                          {editTargetId === reply.id ? (
                            <div className="d-flex flex-column gap-2 mt-2">
                              <textarea className="form-control form-control-sm" rows="2" value={editText} onChange={e => setEditText(e.target.value)}></textarea>
                              <div className="d-flex justify-content-end gap-2">
                                <button className="btn btn-sm text-white rounded-1 px-2 fw-bold shadow-sm" style={{ backgroundColor: '#4c6ef5', fontSize: '11px', height: '26px', lineHeight: '1' }} onClick={() => saveEdit(reply.id)}>저장</button>
                                <button className="btn btn-sm text-white rounded-1 px-2 fw-bold shadow-sm" style={{ backgroundColor: '#868e96', fontSize: '11px', height: '26px', lineHeight: '1' }} onClick={() => setEditTargetId(null)}>취소</button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-dark lh-base mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                              {reply.targetMention && <span className="mention-text me-1">@{reply.targetMention}</span>}
                              {reply.content}
                            </div>
                          )}
                          {renderInlineReplyBox(reply.id)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 스마트 입력창 (왼쪽 패널처럼 화이트톤 + 꽉 찬 통합 박스) */}
      <div className="px-3 bg-white border-top feedback-container" style={{ paddingTop: '12px', paddingBottom: '12px' }}>

        <div className="unified-input-box d-flex flex-column">
          {/* 텍스트 입력 영역 (테두리 없음, 내부 여백 빵빵하게) */}
          <textarea
            ref={inputRef}
            className="form-control border-0 shadow-none px-3 pt-3 pb-2"
            rows="2"
            placeholder={isPureWatcher ? "참조자는 코멘트를 등록할 수 없습니다." : "새로운 피드를 입력하세요..."}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            style={{ resize: 'none', backgroundColor: 'transparent', fontSize: '12px' }}
            disabled={isPureWatcher}
          ></textarea>

          {/* 컨트롤 및 전송 버튼 영역 (입력 박스 하단에 밀착) */}
          <div className="d-flex justify-content-between align-items-center px-3 pb-2 pt-2 border-top">
            <div className="d-flex align-items-center gap-2">
              <div className="btn-group btn-group-sm shadow-sm" role="group">
                <input type="radio" className="btn-check" id="type-general" checked={commentType === 'general'} onChange={() => setCommentType('general')} disabled={isPureWatcher} />
                <label className={`btn btn-outline-custom-gen mb-0 ${isPureWatcher ? 'opacity-50 cursor-not-allowed' : ''}`} htmlFor="type-general">일반</label>

                <input type="radio" className="btn-check" id="type-notice" checked={commentType === 'notice'} onChange={() => setCommentType('notice')} disabled={isPureWatcher} />
                <label className={`btn btn-outline-custom-not mb-0 ${isPureWatcher ? 'opacity-50 cursor-not-allowed' : ''}`} htmlFor="type-notice">공지</label>

                <input type="radio" className="btn-check" id="type-request" checked={commentType === 'request'} onChange={() => setCommentType('request')} disabled={isPureWatcher} />
                <label className={`btn btn-outline-custom-req mb-0 ${isPureWatcher ? 'opacity-50 cursor-not-allowed' : ''}`} htmlFor="type-request">요청</label>
              </div>

              {commentType === 'request' && (
                <div className="position-relative">
                  <button
                    className="btn btn-sm btn-light text-primary fw-bold px-3 py-1 shadow-sm border"
                    onClick={() => setShowMemberPopover(!showMemberPopover)}
                  >
                    {targetUserId.length === 0 ? '✨ 전체 대상' : `담당자 지정 (${targetUserId.length}명)`}
                    <i className="fas fa-chevron-up ms-2"></i>
                  </button>

                  {showMemberPopover && (
                    <div className="position-absolute shadow-lg bg-white rounded-3 p-0" style={{ bottom: '100%', left: 0, marginBottom: '8px', width: '280px', zIndex: 1050, border: '1px solid #c2d7fa', overflow: 'hidden' }}>
                      <div className="p-2 border-bottom" style={{ backgroundColor: '#e8f0fe' }}>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                          <input type="text" className={`form-control border-start-0 ps-0 shadow-none ${memberSearchTerm ? 'border-end-0' : ''}`} placeholder="담당자를 입력하세요" value={memberSearchTerm} onChange={(e) => setMemberSearchTerm(e.target.value)} />
                          {memberSearchTerm && (
                            <span className="input-group-text bg-white border-start-0 cursor-pointer" onClick={() => setMemberSearchTerm('')}>
                              <i className="fas fa-times-circle text-muted" style={{ opacity: 0.6 }}></i>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {/* 전체 대상 항목 */}
                        <div className={`d-flex align-items-center px-2 py-1 mb-1 rounded ${targetUserId.length === 0 ? '' : 'hover-bg-light'}`} style={{ cursor: 'pointer', backgroundColor: targetUserId.length === 0 ? '#e8f0fe' : 'transparent', border: targetUserId.length === 0 ? '1px solid #d2e3fc' : '1px solid transparent' }} onClick={() => toggleTargetSelection(null)}>
                          <div className="flex-shrink-0 me-2">
                            {targetUserId.length === 0 ? (
                              <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '24px', height: '24px' }}>
                                <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                              </div>
                            ) : (
                              <div className="rounded-circle d-flex align-items-center justify-content-center bg-light text-secondary fw-bold shadow-sm border" style={{ width: '24px', height: '24px', fontSize: '10px' }}>
                                All
                              </div>
                            )}
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <div className={`fw-bold fs-8 text-truncate ${targetUserId.length === 0 ? 'text-primary' : 'text-dark'}`}>✨ 전체 대상</div>
                          </div>
                        </div>

                        {/* 개별 멤버 목록 */}
                        {(allProjectMembers || [])
                          .filter(u => u.id !== currentUser?.id)
                          .filter(u => !memberSearchTerm || u.name.includes(memberSearchTerm))
                          .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
                          .map(u => {
                            const isSelected = targetUserId.includes(u.id);
                            const position = u.org?.position || '';
                            const department = u.org?.dept_name ? `[${u.org.dept_name}]` : '';
                            return (
                              <div key={u.id} className={`d-flex align-items-center px-2 py-1 mb-0 rounded ${isSelected ? '' : 'hover-bg-light'}`} style={{ cursor: 'pointer', backgroundColor: isSelected ? '#e8f0fe' : 'transparent', border: isSelected ? '1px solid #d2e3fc' : '1px solid transparent' }} onClick={() => toggleTargetSelection(u)}>
                                <div className="flex-shrink-0 me-2">
                                  {isSelected ? (
                                    <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white shadow-sm" style={{ width: '24px', height: '24px' }}>
                                      <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                                    </div>
                                  ) : (
                                    <Avatar name={u.name} photo={u.org?.photo_path || u.photo_path} size={24} />
                                  )}
                                </div>
                                <div className="flex-grow-1 min-w-0">
                                  <div className={`fw-bold fs-8 text-truncate ${isSelected ? 'text-primary' : 'text-dark'}`}>
                                    {u.name} {position} {department}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      <div className="p-2 border-top d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e8f0fe' }}>
                        <span className="fs-8 text-muted fw-bold">선택된 수: <strong className="text-primary ms-1">{targetUserId.length}</strong></span>
                        <button className="btn btn-sm btn-primary py-1 px-3 fs-8 fw-bold" onClick={() => setShowMemberPopover(false)}>선택 완료</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button className="btn btn-primary btn-sm px-4 fw-bold shadow-sm rounded" onClick={handleSubmit} disabled={isPureWatcher || !inputText.trim()}>
              <i className="fas fa-paper-plane me-1"></i> 등록
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
